<script setup lang="ts">
import {
  computed,
  ref,
  toRef,
} from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useCopyToClipboard } from '@modules/core/composables/copy';
import VFormFloatLabelInputWrapper
  from '@modules/forms/components/inputs/VFormFloatLabelInputWrapper/VFormFloatLabelInputWrapper.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';

import type {
  VFormTextInputEmits,
  VFormTextInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormTextInputProps>(), {
  autocompleteOptions: () => ([]),
});
const emit = defineEmits<VFormTextInputEmits>();

const { innerValue, setValue } = useInputInnerValue(toRef(props, 'value'));
const { copyToClipboard } = useCopyToClipboard();

function clearValue(): void {
  setValue('');
  emit('input', innerValue.value);
  emit('clear', innerValue.value);
}

const autocompleteOptionsFiltered = computed<string[]>(() => {
  if (props.autocompleteServerFiltering) {
    return props.autocompleteOptions;
  }
  return props.autocompleteOptions.filter((item) => !innerValue.value || item.toLowerCase().includes(innerValue.value.toLowerCase()));
});

const autocompleteAttribute = computed(() => {
  if (props.autocompleteOptions.length) {
    return 'off';
  }

  return props.autocomplete ? props.autocomplete : 'off';
});

const isAutoCompleteVisible = ref(false);

const shouldShowAutoComplete = computed<boolean>(() => isAutoCompleteVisible.value && autocompleteOptionsFiltered.value.length > 0);

function onFocus(): void {
  isAutoCompleteVisible.value = true;
  emit('focus', innerValue.value);
}

function onBlur(): void {
  emit('blur', innerValue.value);
  isAutoCompleteVisible.value = false;
}

function onAutocompleteSelect(option: string): void {
  setValue(option);
  emit('input', innerValue.value);
}

function copyValue() {
  if (innerValue.value) {
    void copyToClipboard(innerValue.value);
  }
}
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormTextInput'"
    v-data-test="{ el: 'text-input', name }"
    :errors="errors"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    :hide-errors="hideErrors"
    :readonly="readonly"
    :description="description"
    :description-severity="descriptionSeverity"
  >
    <VFormFloatLabelInputWrapper
      :name="name"
      :label="label"
      :icon-left="iconLeft"
      :icon-right="iconRight"
      :not-float-label="notFloatLabel"
      :autocomplete-enabled="shouldShowAutoComplete"
      :required="required"
      :readonly="readonly"
      :image="image"
      class="relative"
    >
      <PInputText
        v-model="innerValue"
        :name="name"
        :placeholder="placeholder"
        :type="inputType"
        :required="required"
        :input-id="name"
        :readonly="readonly"
        :disabled="disabled"
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly, 'input-readonly': readonly }"
        :autocomplete="autocompleteAttribute"
        @blur="onBlur"
        @focus="onFocus"
        @change="emit('change', innerValue)"
        @input="emit('input', innerValue)"
      />
      <div
        v-if="allowClear && innerValue"
        class="absolute inset-y-0 right-1 z-10 flex items-center"
      >
        <VIconButton
          :disabled="disabled || readonly"
          icon="pi-times"
          @click.stop="clearValue"
        />
      </div>
      <div
        v-if="copy && innerValue && !allowClear"
        class="absolute inset-y-0 right-1 z-10 flex items-center"
      >
        <VIconButton
          icon="pi-copy"
          @click.stop="copyValue"
        />
      </div>
      <div
        v-if="shouldShowAutoComplete"
        class="absolute top-full z-10 max-h-80 w-full overflow-y-auto bg-white shadow-lg"
      >
        <VLoader
          :loading="isAutocompleteOptionsLoading"
          mode="overlay"
        >
          <div
            v-for="option in autocompleteOptionsFiltered"
            :key="option"
            class="cursor-pointer p-4 hover:bg-surface-200"
            @mousedown="onAutocompleteSelect(option)"
          >
            {{ option }}
          </div>
        </VLoader>
      </div>
    </VFormFloatLabelInputWrapper>
  </VFormInputWrapper>
</template>
